/**
 * Print-only page for the SmartInspect Executive Report, rendered at
 * `/print/smartinspect/:token`. Loaded by the backend Playwright renderer.
 *
 * Reuses the EXACT SmartSuite print framework: `PrintReport.css`
 * (.print-root / .print-brand / .print-footer, page-break rules, page size),
 * the branded header (platform logo), the confidentiality footer + version
 * number, and the `data-print-ready` handshake. Only the report body differs
 * (the SmartInspect-specific `<SmartInspectExecutiveReport>`).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { SmartInspectExecutiveReport } from "../consultant/SmartInspectExecutiveReport";
import "./PrintReport.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PrintSmartInspectReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/smartinspect/print/data/${token}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load report data");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (async () => {
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch { /* noop */ }
      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => setTimeout(r, 600));
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [data]);

  if (error) return <div className="print-error">Unable to load report: {error}</div>;
  if (!data) return <div className="print-loading">Loading report…</div>;

  const snapshot = data.version?.snapshot || {};
  const versionNumber = data.version?.version_number || 1;
  const companyName = snapshot.metadata?.company_name || "Client";
  const branding = data.branding || {};
  const footerFirm = branding.footer_text || "PrimeS4 Consulting";
  const footerNotice = branding.report_confidentiality_notice || "Confidential · for client discussion only";
  const platformLogo = branding.partner_logo || branding.platform_logo || "";
  const liveAssessment = data.assessment || {};
  const clientLogoDataUrl = liveAssessment.client_logo_base64
    ? `data:${liveAssessment.client_logo_mime || "image/png"};base64,${liveAssessment.client_logo_base64}`
    : "";

  return (
    <div className="print-root" data-print-ready={ready ? "true" : undefined}>
      <header className="print-brand">
        <div className="print-brand-left">
          {platformLogo && (
            <img loading="lazy" src={platformLogo} alt="Platform logo" className="print-brand-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
        </div>
        <div className="print-brand-right">
          {clientLogoDataUrl && (
            <img loading="lazy" src={clientLogoDataUrl} alt={`${companyName} logo`} className="print-client-logo" />
          )}
        </div>
      </header>

      <main className="print-body">
        <SmartInspectExecutiveReport snapshot={snapshot} />
      </main>

      <footer className="print-footer">
        <div className="print-footer-left">Prepared for {companyName} · {footerFirm}</div>
        <div className="print-footer-center" data-testid="print-footer-version">v{versionNumber}</div>
        <div className="print-footer-right print-footer-muted">{footerNotice} · {new Date().getFullYear()}</div>
      </footer>
    </div>
  );
}
